import gql from 'graphql-tag';
import {
  AuthenticateWithPhoneInput,
  AuthenticateWithPhoneMutation,
  AuthenticationInput,
  CreateAddressInput,
  GetActiveCustomerQuery,
  LoginMutation,
  LogoutMutation,
  RegisterCustomerAccountMutation,
  RegisterCustomerAccountMutationVariables,
  RequestPhoneNumberOtpMutation,
  RequestUpdateCustomerPhoneNumberMutation,
  ResetPasswordResult,
  UpdateActiveCustomerMutation,
  UpdateAddressInput,
  UpdateCustomerInput,
  UpdateCustomerPhoneNumberMutation,
  VerifyCustomerAccountMutation,
  VerifyPhoneAuthInput,
  VerifyPhoneAuthMutation,
} from '~/generated/graphql';
import { QueryOptions, sdk, WithHeaders } from '~/graphqlWrapper';

export const login = async (
  email: string,
  password: string,
  rememberMe: boolean,
  options: QueryOptions,
): Promise<WithHeaders<LoginMutation['login']>> => {
  return sdk.login({ email, password, rememberMe }, options).then((res) => ({
    ...res.login,
    _headers: res._headers,
  }));
};

export function authenticate(
  input: AuthenticationInput,
  options: QueryOptions,
) {
  return sdk.authenticate({ input }, options);
}

export const logout = async (
  options: QueryOptions,
): Promise<WithHeaders<LogoutMutation['logout']>> => {
  return sdk.logout({}, options).then((res) => ({
    ...res.logout,
    _headers: res._headers,
  }));
};

export const registerCustomerAccount = async (
  options: QueryOptions,
  variables: RegisterCustomerAccountMutationVariables,
): Promise<
  WithHeaders<RegisterCustomerAccountMutation['registerCustomerAccount']>
> => {
  return sdk.registerCustomerAccount(variables, options).then((res) => ({
    ...res.registerCustomerAccount,
    _headers: res._headers,
  }));
};

export const verifyCustomerAccount = async (
  options: QueryOptions,
  token: string,
  password?: string,
): Promise<
  WithHeaders<VerifyCustomerAccountMutation['verifyCustomerAccount']>
> => {
  return sdk
    .verifyCustomerAccount({ token, password }, options)
    .then((res) => ({
      ...res.verifyCustomerAccount,
      _headers: res._headers,
    }));
};

export async function updateCustomer(
  input: UpdateCustomerInput,
  options: QueryOptions,
) {
  return sdk.updateCustomer({ input }, options);
}

export async function requestUpdateCustomerEmailAddress(
  password: string,
  newEmailAddress: string,
  options: QueryOptions,
) {
  return sdk
    .requestUpdateCustomerEmailAddress({ password, newEmailAddress }, options)
    .then((res) => res.requestUpdateCustomerEmailAddress);
}

export async function updateCustomerEmailAddress(
  token: string,
  options: QueryOptions,
) {
  return sdk
    .updateCustomerEmailAddress({ token }, options)
    .then((res) => res.updateCustomerEmailAddress);
}

export async function updateCustomerAddress(
  input: UpdateAddressInput,
  options: QueryOptions,
) {
  return sdk
    .updateCustomerAddress({ input }, options)
    .then((res) => res.updateCustomerAddress);
}

export async function createCustomerAddress(
  input: CreateAddressInput,
  options: QueryOptions,
) {
  return sdk
    .createCustomerAddress({ input }, options)
    .then((res) => res.createCustomerAddress);
}

export async function deleteCustomerAddress(id: string, options: QueryOptions) {
  return sdk
    .deleteCustomerAddress({ id }, options)
    .then((res) => res.deleteCustomerAddress);
}

export async function updateCustomerPassword(
  input: { currentPassword: string; newPassword: string },
  options: QueryOptions,
) {
  return sdk
    .updateCustomerPassword(input, options)
    .then((res) => res.updateCustomerPassword);
}

export async function requestPasswordReset(
  emailAddress: string,
  options: QueryOptions,
) {
  return sdk
    .requestPasswordReset({ emailAddress }, options)
    .then((res) => res.requestPasswordReset);
}

export const resetPassword = async (
  token: string,
  password: string,
  options: QueryOptions,
): Promise<ResetPasswordResult> => {
  return sdk
    .resetPassword({ token, password }, options)
    .then((res) => res.resetPassword as ResetPasswordResult);
};

export async function requestPhoneNumberOtp(
  phoneNumber: string,
  options: QueryOptions,
): Promise<WithHeaders<RequestPhoneNumberOtpMutation['requestPhoneNumberOtp']>> {
  return sdk
    .requestPhoneNumberOtp({ phoneNumber }, options)
    .then((res) => ({
      ...res.requestPhoneNumberOtp,
      _headers: res._headers,
    }));
}

export async function requestResendPhoneNumberOtp(
  phoneNumber: string,
  options: QueryOptions,
) {
  return sdk
    .requestResendPhoneNumberOtp({ phoneNumber }, options)
    .then((res) => res.requestResendPhoneNumberOtp);
}

export async function requestUpdateCustomerPhoneNumber(
  input: { code: string; phoneNumber: string },
  newPhoneNumber: string,
  options: QueryOptions,
): Promise<WithHeaders<RequestUpdateCustomerPhoneNumberMutation['requestUpdateCustomerPhoneNumber']>> {
  return sdk
    .requestUpdateCustomerPhoneNumber({ input, newPhoneNumber }, options)
    .then((res) => ({
      ...res.requestUpdateCustomerPhoneNumber,
      _headers: res._headers,
    }));
}

export async function updateCustomerPhoneNumber(
  input: { phoneNumber: string; code: string },
  options: QueryOptions,
): Promise<WithHeaders<UpdateCustomerPhoneNumberMutation['updateCustomerPhoneNumber']>> {
  return sdk
    .updateCustomerPhoneNumber({ input }, options)
    .then((res) => ({
      ...res.updateCustomerPhoneNumber,
      _headers: res._headers,
    }));
}

export async function registerCustomerWithPhoneNumber(
  input: {
    phoneNumber: string;
    emailAddress: string;
    firstName?: string;
    lastName?: string;
  },
  options: QueryOptions,
) {
  return sdk
    .registerCustomerWithPhoneNumber({ input }, options)
    .then((res) => res.registerCustomerWithPhoneNumber);
}

// Authenticate with phone number (Step 1: Send OTP)
export async function authenticateWithPhone(
  input: AuthenticateWithPhoneInput,
  options: QueryOptions,
): Promise<WithHeaders<AuthenticateWithPhoneMutation['authenticateWithPhone']>> {
  return sdk
    .authenticateWithPhone({ input }, options)
    .then((res) => ({
      ...res.authenticateWithPhone,
      _headers: res._headers,
    }));
}

// Verify phone authentication (Step 2: Verify OTP)
export async function verifyPhoneAuth(
  input: VerifyPhoneAuthInput,
  options: QueryOptions,
): Promise<WithHeaders<VerifyPhoneAuthMutation['verifyPhoneAuth']>> {
  return sdk
    .verifyPhoneAuth({ input }, options)
    .then((res) => ({
      ...res.verifyPhoneAuth,
      _headers: res._headers,
    }));
}

// Get active customer with KYC custom fields
export async function getActiveCustomer(
  options: QueryOptions,
): Promise<GetActiveCustomerQuery['activeCustomer']> {
  return sdk
    .GetActiveCustomer({}, options)
    .then((res) => res.activeCustomer);
}

// Update active customer with KYC custom fields
export async function updateActiveCustomer(
  input: UpdateCustomerInput,
  options: QueryOptions,
): Promise<UpdateActiveCustomerMutation['updateCustomer']> {
  return sdk
    .UpdateActiveCustomer({ input }, options)
    .then((res) => res.updateCustomer);
}

gql`
  mutation login($email: String!, $password: String!, $rememberMe: Boolean) {
  login(username: $email, password: $password, rememberMe: $rememberMe) {
    __typename
      ... on CurrentUser {
      id
      identifier
    }
      ... on ErrorResult {
      errorCode
      message
    }
  }
}
`;

gql`
  mutation logout {
    logout {
    success
  }
}
`;

gql`
  mutation registerCustomerAccount($input: RegisterCustomerInput!) {
  registerCustomerAccount(input: $input) {
    __typename
      ... on Success {
      success
    }
      ... on ErrorResult {
      errorCode
      message
    }
  }
}
`;

gql`
  mutation verifyCustomerAccount($token: String!, $password: String) {
  verifyCustomerAccount(token: $token, password: $password) {
    __typename
      ... on CurrentUser {
      id
      identifier
    }
      ... on ErrorResult {
      errorCode
      message
    }
  }
}
`;

gql`
  mutation updateCustomer($input: UpdateCustomerInput!) {
  updateCustomer(input: $input) {
    __typename
  }
}
`;

gql`
  mutation requestUpdateCustomerEmailAddress(
  $password: String!
    $newEmailAddress: String!
) {
  requestUpdateCustomerEmailAddress(
    password: $password
      newEmailAddress: $newEmailAddress
  ) {
    __typename
      ... on ErrorResult {
      errorCode
      message
    }
  }
}
`;

gql`
  mutation updateCustomerEmailAddress($token: String!) {
  updateCustomerEmailAddress(token: $token) {
    __typename
      ... on ErrorResult {
      errorCode
      message
    }
  }
}
`;

gql`
  mutation updateCustomerAddress($input: UpdateAddressInput!) {
  updateCustomerAddress(input: $input) {
    __typename
  }
}
`;

gql`
  mutation createCustomerAddress($input: CreateAddressInput!) {
  createCustomerAddress(input: $input) {
    __typename
  }
}
`;

gql`
  mutation deleteCustomerAddress($id: ID!) {
  deleteCustomerAddress(id: $id) {
    success
  }
}
`;

gql`
  mutation updateCustomerPassword(
  $currentPassword: String!
    $newPassword: String!
) {
  updateCustomerPassword(
    currentPassword: $currentPassword
      newPassword: $newPassword
  ) {
    __typename
      ... on Success {
      success
    }
      ... on ErrorResult {
      errorCode
      message
    }
  }
}
`;

gql`
  mutation requestPasswordReset($emailAddress: String!) {
  requestPasswordReset(emailAddress: $emailAddress) {
    __typename
      ... on Success {
      success
    }
      ... on ErrorResult {
      errorCode
      message
    }
  }
}
`;

gql`
  mutation resetPassword($token: String!, $password: String!) {
  resetPassword(token: $token, password: $password) {
    __typename
      ... on CurrentUser {
      id
      identifier
    }
      ... on ErrorResult {
      errorCode
      message
    }
  }
}
`;

gql`
  mutation requestPhoneNumberOtp($phoneNumber: String!) {
  requestPhoneNumberOtp(phoneNumber: $phoneNumber) {
    __typename
      ... on ErrorResult {
      errorCode
      message
    }
  }
}
`;

gql`
  mutation requestResendPhoneNumberOtp($phoneNumber: String!) {
  requestResendPhoneNumberOtp(phoneNumber: $phoneNumber) {
    __typename
      ... on ErrorResult {
      errorCode
      message
    }
  }
}
`;

gql`
  mutation requestUpdateCustomerPhoneNumber(
  $input: RequestUpdateCustomerPhoneNumberInput!
    $newPhoneNumber: String!
) {
  requestUpdateCustomerPhoneNumber(input: $input, newPhoneNumber: $newPhoneNumber) {
    __typename
  }
}
`;

gql`
  mutation updateCustomerPhoneNumber($input: VerifyPhoneNumberOtpInput!) {
  updateCustomerPhoneNumber(input: $input) {
    __typename
  }
}
`;

gql`
  mutation registerCustomerWithPhoneNumber(
  $input: RegisterCustomerWithPhoneNumberInput!
) {
  registerCustomerWithPhoneNumber(input: $input) {
      ... on Success {
      success
    }
      ... on ErrorResult {
      errorCode
      message
    }
  }
}
`;

gql`
  mutation authenticateWithPhone($input: AuthenticateWithPhoneInput!) {
  authenticateWithPhone(input: $input) {
    __typename
      ... on AuthInitiationResult {
      success
      isNewUser
      message
    }
      ... on ErrorResult {
      errorCode
      message
    }
  }
}
`;

gql`
  mutation verifyPhoneAuth($input: VerifyPhoneAuthInput!) {
  verifyPhoneAuth(input: $input) {
    __typename
      ... on PhoneAuthenticationResult {
        currentUser {
        id
        identifier
      }
    }
      ... on ErrorResult {
      errorCode
      message
    }
  }
}
`;

gql`
  mutation authenticate($input: AuthenticationInput!) {
  authenticate(input: $input) {
    __typename
      ... on CurrentUser {
      id
      identifier
    }
      ... on ErrorResult {
      errorCode
      message
    }
  }
}
`;

gql`
  query GetActiveCustomer {
    activeCustomer {
    id
    firstName
    lastName
    emailAddress
      customFields {
      businessName
      businessType
      registrationNumber
      physicalAddress
      yearsInOperation
      ownerFullName
      ownerEmail
      ownerPhoneNumber
      governmentIDType
      governmentIDNumber
      kycStatus
        governmentIDFile {
        id
        name
        preview
        source
      }
        financialStatementFile {
        id
        name
        preview
        source
      }
        salesRecordFile {
        id
        name
        preview
        source
      }
        premisesPhoto1 {
        id
        name
        preview
        source
      }
        premisesPhoto2 {
        id
        name
        preview
        source
      }
        premisesPhoto3 {
        id
        name
        preview
        source
      }
    }
  }
}
`;

gql`
  mutation UpdateActiveCustomer($input: UpdateCustomerInput!) {
  updateCustomer(input: $input) {
    id
    firstName
    lastName
      customFields {
      businessName
      businessType
      registrationNumber
      physicalAddress
      yearsInOperation
      ownerFullName
      ownerEmail
      ownerPhoneNumber
      governmentIDType
      governmentIDNumber
      kycStatus
        governmentIDFile {
        id
        name
        preview
        source
      }
        financialStatementFile {
        id
        name
        preview
        source
      }
        salesRecordFile {
        id
        name
        preview
        source
      }
        premisesPhoto1 {
        id
        name
        preview
        source
      }
        premisesPhoto2 {
        id
        name
        preview
        source
      }
        premisesPhoto3 {
        id
        name
        preview
        source
      }
    }
  }
}
`;
